import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIf, NgOptimizedImage } from '@angular/common';

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule, MatMiniFabButton } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatRipple, MatRippleModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableModule,
} from '@angular/material/table';

import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {MatPaginator, MatPaginatorModule, PageEvent} from '@angular/material/paginator';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import {firstValueFrom, fromEvent, Subject, Subscription} from 'rxjs';

import { DefaultResponse } from '@core/models/Http/DefaultResponse';
import { PatientDetails } from '@core/models/PatientDetails';
import { FilterRecetaPatient } from '@core/models/FilterRecetaPatient';
import { Prescription } from '@core/models/Prescription';

import { TableElement, TableExportUtil, UnsubscribeOnDestroyAdapter } from '@shared';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';

import { CreateEditService } from '@core/http/create-edit-service';
import { PrescriptionService } from '@core/http/table-data-services/prescription.service';
import { PrescriptionbyIdPatientDataSource } from '@core/data-source/PrescriptionbyIdPatientDataSource';

import { AuthManagementService } from '@core/service/auth-management.service';
import { CatalogsService } from '@core/http/catalogs.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';

import { DoDeleteComponent } from '@shared/components/do-delete/do-delete.component';
import { UserRole } from '@core/models/Enums/UserRole';
import { PrescriptionQrComponent } from '../prescription-qr/prescription-qr.component';
import { PatientPinDialogComponent } from '@shared/components/patient-pin-dialog/patient-pin-dialog.component';

import { TutorService } from '@core/service/tutor.service';
import { UserService } from '@core/http/table-data-services/User.service';
import {downloadPrescriptionPdf, generatePrescriptionPdfBase64, openPrintWindow} from "@shared/utils/window.utils";
import {RepositorioPacienteResponse} from "@core/interfaces/repositorio-paciente";
import {MsgResponse} from "@core/models/MsgResponse";
import {environment} from "@enviroment/environment";
import Swal from "sweetalert2";

type TutorPacienteVm = {
  idPaciente: string;
  idUsuario: string;
  nombreCompleto: string;
  imagenSrc: string | null;
  initials: string;
};

@Component({
  selector: 'app-prescription-by-id-patient',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgOptimizedImage,

    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,

    MatIcon,
    MatIconModule,
    MatButtonModule,
    MatMiniFabButton,
    MatTooltip,
    MatTooltipModule,
    MatTable,
    MatTableModule,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatRipple,
    MatRippleModule,
    MatSort,
    MatSortModule,
    MatPaginator,
    MatPaginatorModule,
    MatProgressSpinnerModule,

    FeatherIconsComponent,
  ],
  templateUrl: './prescription-by-id-patient.component.html',
  styleUrl: './prescription-by-id-patient.component.scss',
})
export class PrescriptionByIdPatientComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit
{
  @Input() param!: string;
  @Input() control: number = 0;
  @Input() embeddedInProfile: boolean = false;

  displayedColumns = [
    'folio',
    'medico',
    'gemp',
    'creacion',
    'status',
    'detalles',
    'descargar',
  ];

  data?: PrescriptionService;
  dataSource: PrescriptionbyIdPatientDataSource;

  isAdmin: boolean;
  isPatient: boolean;

  filterReceta: FilterRecetaPatient;

  // ===== Tutor UI =====
  showTutorSelect = false;
  tutorPacientes: TutorPacienteVm[] = [];
  selectedPacienteId: string | null = null;
  selectedPacienteVm: TutorPacienteVm | null = null;
  hipLoading = false;
  readonly showHipFeatures = environment.showHipFeatures;

  currentMonthLabel = new Date().toLocaleDateString('es-MX', {
    month: 'long',
    year: 'numeric'
  }).toUpperCase();

  private loggedPacienteId: string | null = null;
  private loggedPacienteResolved = false;

  private readonly imageCache = new Map<string, string>();

  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild('filter', { static: true }) filter?: ElementRef;

  protected readonly UserRole = UserRole;

  private searching = false;
  private lastSearchAt = 0;
  private readonly minSearchIntervalMs = 600;

  // Evitar listeners duplicados del input search
  private filterKeyupSub?: Subscription;

  constructor(
    private ceService: CreateEditService,
    public httpClient: HttpClient,
    public dialog: MatDialog,
    private authManagement: AuthManagementService,
    private catalogsService: CatalogsService,
    private router: Router,
    private cd: ChangeDetectorRef,
    private service: PrescriptionService,
    private httpMsgHandler: HttpMesageHandlerService,
    private tutorService: TutorService,
    private userService: UserService
  ) {
    super();

    this.isAdmin = this.authManagement.isUserAdmin();
    this.isPatient = this.authManagement.isUserPatient();

    this.filterReceta = new FilterRecetaPatient({} as FilterRecetaPatient);

    if (this.isAdmin) this.displayedColumns.push('acciones');
    if (this.isPatient) this.displayedColumns.push('mostrarQr');

    this.data = new PrescriptionService(this.httpClient, this.httpMsgHandler);

    const paginatorStub = {
      pageIndex: 0,
      pageSize: 10,
      page: new Subject<PageEvent>()
    } as MatPaginator;

    const sortStub = {
      active: '',
      direction: '',
      sortChange: new Subject<Sort>()
    } as MatSort;

    this.dataSource = new PrescriptionbyIdPatientDataSource(
      this.data,
      paginatorStub,
      sortStub,
      {} as FilterRecetaPatient
    );
  }

  ngOnInit() {
    this.filterReceta.dateFilter = 'Mes';
    this.filterReceta.startDate = null;
    this.filterReceta.endDate = null;

    this.initScreen();

    if (this.isPatient) {
      this.tryLoadTutorPacientes();
    }
  }

  private initScreen(): void {

    // ✅ VIENE DESDE PERFIL (ADMIN/MEDICO): param = idUsuario del paciente
    if (this.param) {
      this.ceService.getpatientByIdUser(this.param).subscribe({
        next: (res: any) => {
          const payload = res?.data ?? null;
          const row = Array.isArray(payload) ? (payload[0] ?? null) : payload;

          const idPaciente = (row?.idPaciente ?? row?.IdPaciente ?? '').toString().trim();

          this.filterReceta.idPaciente = idPaciente || null;

          if (!this.filterReceta.idPaciente) {
            return;
          }

          this.loadData(this.filterReceta);
        },
        error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
      });

      return;
    }

    // ✅ PACIENTE LOGUEADO (sin param)
    this.ensureLoggedPacienteId()
      .then(() => {
        this.filterReceta.idPaciente = this.resolveIdPacienteForList();
        this.loadData(this.filterReceta);
      })
      .catch(() => {
        this.service.dataChange.next([]);
      });
  }

  // =========================================================
  // ✅ Resuelve loggedPacienteId UNA SOLA VEZ
  // =========================================================
  private async ensureLoggedPacienteId(): Promise<void> {
    if (this.loggedPacienteResolved) return;

    const userData = this.authManagement.userData();
    const idUsuario = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idUsuario) {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
      return;
    }

    try {
      const res: any = await firstValueFrom(this.ceService.getpatientByIdUser(idUsuario));

      const payload = res?.data ?? null;
      const row = Array.isArray(payload) ? payload[0] : payload;

      const idPaciente = (row?.idPaciente ?? row?.IdPaciente ?? '').toString().trim();

      this.loggedPacienteId = idPaciente || null;
      this.loggedPacienteResolved = true;
    } catch {
      this.loggedPacienteId = null;
      this.loggedPacienteResolved = true;
    }
  }

  refresh() {
    this.buscar(true);
  }

  // =========================================================
  // ✅ Para LISTADO: decide si mandamos idPaciente o null
  // - Admin/Doctor (param): SIEMPRE mandar idPaciente
  // - Paciente:
  //   - si hay selección del tutor => mandar selectedPacienteId
  //   - si NO hay selección => null (token mode)
  // =========================================================
  private resolveIdPacienteForList(): string | null {
    // Admin/Doctor: si ya tienes idPaciente en filterReceta (viene por param), lo mandas
    if (!this.isPatient) {
      return (this.filterReceta.idPaciente ?? '').toString().trim() || null;
    }

    // Paciente:
    const selected = (this.selectedPacienteId ?? '').toString().trim();
    if (this.showTutorSelect && selected) {
      return selected;
    }

    // Paciente normal (o tutor sin selección) => token mode
    return null;
  }


  exportExcel() {
    const exportData: Partial<TableElement>[] = this.dataSource.filteredData.map(
      (x: Prescription) => ({ ID: x.idReceta })
    );
    TableExportUtil.exportToExcel(exportData, 'excel');
  }

  // =========================================================
  // ✅ DATA SOURCE + SEARCH BOX (sin listeners duplicados)
  // =========================================================
  public loadData(filterReceta: FilterRecetaPatient): void {
    this.data = new PrescriptionService(this.httpClient, this.httpMsgHandler);

    this.dataSource = new PrescriptionbyIdPatientDataSource(
      this.data,
      this.paginator,
      this.sort,
      filterReceta
    );

    this.dataSource.filter = '';

    if (this.filter?.nativeElement) {
      this.filter.nativeElement.value = '';
    }

    this.filterKeyupSub?.unsubscribe();

    if (this.filter?.nativeElement) {
      const input = this.filter.nativeElement;

      this.filterKeyupSub = fromEvent<KeyboardEvent>(input, 'keyup')
        .subscribe(() => {
          this.dataSource.filter = input.value ?? '';
        });
    }

    this.data.getPrescriptionByIdPatient(filterReceta);

    this.cd.detectChanges();
  }

  // =========================================================
  // ✅ FILTROS FECHA
  // IMPORTANTE: ya NO disparamos buscar aquí para evitar 429
  // =========================================================
  onDateChange() {
    this.filterReceta.dateFilter = null;
  }

  onDateFilterChange() {
    // no auto-buscar
  }

  clearDate(field: 'startDate' | 'endDate') {
    (this.filterReceta as any)[field] = null;

    this.filterReceta.dateFilter = null;
  }

  // =========================================================
// ✅ BUSCAR (con candado + throttle)
// - Si hay rango (start/end) => dateFilter = null
// - Si no hay rango y no hay dateFilter => dateFilter = 'Mes'
// - endDate se manda a 23:59:59.999 para incluir todo el día
// =========================================================
  async buscar(force: boolean = false) {
    const now = Date.now();

    if (!force) {
      if (this.searching) return;
      if (now - this.lastSearchAt < this.minSearchIntervalMs) return;
    }

    this.searching = true;
    this.lastSearchAt = now;

    try {
      // ✅ si es tutor: resolver paciente objetivo
      // ✅ resolver idPaciente para LISTADO
      // - paciente normal => null (token)
      // - tutor con selección => selectedPacienteId
      // - admin/doctor => idPaciente requerido (ya viene en filterReceta por param)
      this.filterReceta.idPaciente = this.resolveIdPacienteForList();

      // ✅ ya NO bloqueamos null porque ahora es el comportamiento esperado

      // =========================
      // ✅ Normalizar filtros:
      // - o rango de fechas
      // - o dateFilter (Mes/Dia/Año)
      // =========================
      const hasStart = !!this.filterReceta.startDate;
      const hasEnd = !!this.filterReceta.endDate;

      if (hasStart || hasEnd) {
        // ✅ si hay rango, dateFilter NO aplica
        this.filterReceta.dateFilter = null;

        if (this.filterReceta.startDate) {
          const s = new Date(this.filterReceta.startDate);
          s.setHours(0, 0, 0, 0);
          this.filterReceta.startDate = s.toISOString();
        }

        if (this.filterReceta.endDate) {
          const e = new Date(this.filterReceta.endDate);
          e.setHours(23, 59, 59, 999);
          this.filterReceta.endDate = e.toISOString();
        }
      } else {
        // ✅ no hay fechas => si tampoco hay dateFilter, default MES
        if (!this.filterReceta.dateFilter) {
          this.filterReceta.dateFilter = 'Mes';
        }

        // ✅ asegúrate que no se manden fechas si estás en dateFilter
        this.filterReceta.startDate = null;
        this.filterReceta.endDate = null;
      }

      if (this.paginator) this.paginator.firstPage();

      this.loadData(this.filterReceta);
      this.cd.detectChanges();
    } finally {
      this.searching = false;
    }
  }

  // =========================================================
  // ✅ TUTOR: lista de pacientes del tutor
  // =========================================================
  private tryLoadTutorPacientes(): void {
    const userData = this.authManagement.userData();
    const idTutor = (userData?.IdUsuario ?? userData?.idUsuario ?? '').toString().trim();

    if (!idTutor) {
      this.showTutorSelect = false;
      return;
    }

    this.tutorService.getPacientesByTutor(idTutor).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res ?? [];
        const list = Array.isArray(payload) ? payload : [];

        if (!list.length) {
          this.showTutorSelect = false;
          this.tutorPacientes = [];
          this.selectedPacienteId = null;
          this.selectedPacienteVm = null;
          return;
        }

        this.showTutorSelect = true;

        this.tutorPacientes = list
          .map((row: any) => {
            const idPaciente = String(row?.idPaciente ?? row?.IdPaciente ?? '').trim();
            const idUsuario = String(row?.idUsuario ?? row?.IdUsuario ?? '').trim();

            const n = (row?.nombres ?? row?.Nombres ?? '').toString();
            const a1 = (row?.primerApellido ?? row?.PrimerApellido ?? '').toString();
            const a2 = (row?.segundoApellido ?? row?.SegundoApellido ?? '').toString();

            const nombreCompleto = `${n} ${a1} ${a2}`.replace(/\s+/g, ' ').trim();
            const initials = this.getInitials(nombreCompleto);

            return {
              idPaciente,
              idUsuario,
              nombreCompleto: nombreCompleto || 'PACIENTE',
              imagenSrc: null,
              initials,
            } as TutorPacienteVm;
          })
          .filter((x: TutorPacienteVm) => !!x.idPaciente);

        // NO auto seleccionar
        this.selectedPacienteId = null;
        this.selectedPacienteVm = null;

        this.tutorPacientes.forEach((p) => this.loadUserImage(p.idUsuario, p.idPaciente));
      },
      error: () => {
        this.showTutorSelect = false;
        this.tutorPacientes = [];
        this.selectedPacienteId = null;
        this.selectedPacienteVm = null;
      },
    });
  }

  onTutorPacienteChange() {
    const idPaciente = (this.selectedPacienteId ?? '').toString().trim();
    if (!idPaciente) {
      this.clearSelectedPaciente();
      return;
    }

    this.filterReceta.idPaciente = idPaciente;
    this.selectedPacienteVm = this.tutorPacientes.find((x) => x.idPaciente === idPaciente) ?? null;

    // ✅ aquí sí buscamos (cambio de paciente)
    this.buscar(true);
  }

  // =========================================================
  // ✅ X: volver a recetas del paciente logueado => token mode (null)
  // =========================================================
  async clearSelectedPaciente(): Promise<void> {
    this.selectedPacienteId = null;
    this.selectedPacienteVm = null;

    // ✅ token mode para paciente logueado
    this.filterReceta.idPaciente = null;

    if (this.paginator) this.paginator.firstPage();

    this.loadData(this.filterReceta);
    this.cd.detectChanges();
  }

  // =========================================================
  // IMÁGENES TUTOR
  // =========================================================
  private loadUserImage(idUsuario: string, idPaciente: string): void {
    if (!idUsuario) return;

    const cached = this.imageCache.get(idUsuario);
    if (cached !== undefined) {
      this.applyImageToVm(idPaciente, cached || null);
      return;
    }

    this.userService.getUserImageBase64(idUsuario).subscribe({
      next: (img: string) => {
        const normalized = (img ?? '').trim();
        const value = normalized ? normalized : '';
        this.imageCache.set(idUsuario, value);
        this.applyImageToVm(idPaciente, value || null);
      },
      error: () => {
        this.imageCache.set(idUsuario, '');
        this.applyImageToVm(idPaciente, null);
      },
    });
  }

  private applyImageToVm(idPaciente: string, img: string | null) {
    const idx = this.tutorPacientes.findIndex((x) => x.idPaciente === idPaciente);
    if (idx < 0) return;

    this.tutorPacientes[idx].imagenSrc = img || null;

    if (this.selectedPacienteVm?.idPaciente === idPaciente) {
      this.selectedPacienteVm = { ...this.tutorPacientes[idx] };
    }

    this.cd.detectChanges();
  }

  private getInitials(name: string): string {
    const parts = (name ?? '').toUpperCase().split(' ').filter(Boolean);
    const a = parts[0]?.[0] ?? 'P';
    const b = parts[1]?.[0] ?? '';
    return (a + b).trim();
  }

  // =========================================================
  // ✅ Decide idPaciente para BACKEND según tu regla REAL
  // - Médico/Admin => manda SIEMPRE un idPaciente (normalmente el del row)
  // - Paciente:
  //   - si el tutor seleccionó paciente => manda selectedPacienteId
  //   - si NO seleccionó => SIEMPRE null (token mode)
  // =========================================================
  private resolveIdPacienteForBackend(rowIdPaciente?: any): string | null {
    // Médico/Admin: el backend requiere idPaciente
    if (!this.isPatient) {
      const rowPaciente = (rowIdPaciente ?? '').toString().trim();
      return rowPaciente || null;
    }

    // Paciente (incluye tutor):
    const selected = (this.selectedPacienteId ?? '').toString().trim();
    if (this.showTutorSelect && selected) {
      return selected;
    }

    // ✅ Paciente normal (o tutor sin selección) => SIEMPRE null
    return null;
  }

  private resolveIdPacienteForHip(): string | null {
    const selected = (this.selectedPacienteId ?? '').toString().trim();
    if (selected) {
      return selected;
    }

    if (!this.isPatient) {
      return (this.filterReceta.idPaciente ?? '').toString().trim() || null;
    }

    return null;
  }

  async descargarHip(): Promise<void> {
    if (this.hipLoading) return;

    const idPaciente = this.resolveIdPacienteForHip();
    this.hipLoading = true;

    try {
      const response = await firstValueFrom(
        this.service.getRepositorioPaciente(idPaciente, null)
      );

      if (this.isErrorOrWarning(response)) {
        this.httpMsgHandler.handleMsg(response as unknown as MsgResponse);
        return;
      }

      const repo = response?.data;
      let pin: string | null | undefined = null;

      if (repo?.hasHidden) {
        pin = await this.requestHipPin(repo);
        if (typeof pin === 'undefined') return;
      }

      const html = await firstValueFrom(
        this.service.getRepositorioPacienteHtml(idPaciente, pin ?? null)
      );

      this.openHipHtmlOrShowError(html);
    } catch (err) {
      this.httpMsgHandler.handleMsg(err as HttpErrorResponse);
    } finally {
      this.hipLoading = false;
    }
  }

  private async requestHipPin(repo: RepositorioPacienteResponse): Promise<string | null | undefined> {
    const hasVisibleMeds = (repo?.tratamientoActivo?.length ?? 0) > 0;
    const mode = !this.isPatient
      ? 'ADMIN'
      : this.selectedPacienteId
        ? 'TUTOR'
        : 'PACIENTE';

    const dialogRef = this.dialog.open(PatientPinDialogComponent, {
      disableClose: false,
      autoFocus: false,
      width: '560px',
      maxWidth: '92vw',
      panelClass: ['rmd-pin-dialog-panel'],
      data: {
        mode,
        title: 'HIP CON MEDICAMENTOS OCULTOS',
        message: hasVisibleMeds
          ? 'EL PACIENTE TIENE MEDICAMENTOS OCULTOS. INGRESA EL PIN PARA INCLUIRLOS EN LA HIP O CONTINUA SIN PIN.'
          : 'EL PACIENTE TIENE MEDICAMENTOS OCULTOS. INGRESA EL PIN PARA GENERAR LA HIP CON EL TRATAMIENTO ACTIVO.',
        optional: true,
      },
    });

    return firstValueFrom(dialogRef.afterClosed());
  }

  private openHipHtmlOrShowError(raw: string): void {
    const html = (raw ?? '').toString();

    if (!html.trim()) {
      Swal.fire('AVISO', 'NO SE RECIBIO HTML PARA LA HIP.', 'info').then();
      return;
    }

    if (!this.looksLikeHtml(html)) {
      const parsed = this.tryParseResponse(html);

      if (parsed) {
        this.httpMsgHandler.handleMsg(parsed as unknown as MsgResponse);
        return;
      }

      Swal.fire('AVISO', 'LA RESPUESTA DE HIP NO TIENE FORMATO HTML.', 'warning').then();
      return;
    }

    this.printHtml(html);
  }

  private looksLikeHtml(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    return normalized.includes('<html') ||
      normalized.includes('<!doctype html') ||
      normalized.includes('<head') ||
      normalized.includes('<body');
  }

  private tryParseResponse(value: string): DefaultResponse<unknown> | null {
    try {
      return JSON.parse(value) as DefaultResponse<unknown>;
    } catch {
      return null;
    }
  }

  private isErrorOrWarning(response: DefaultResponse<unknown> | null | undefined): boolean {
    const toast = (response?.toast ?? '').toString().toLowerCase();
    return toast === 'error' || toast === 'warning';
  }

  // =========================================================
  // ✅ PDF (con PIN si aplica)
  // =========================================================
  async descargarPDF(row: Prescription) {
    const idReceta = (row as any)?.idReceta;
    const idPacienteRow = (row as any)?.idPaciente;

    if (!idReceta) return;

    const idPacienteToSend = this.resolveIdPacienteForBackend(idPacienteRow);

    // Si es médico/admin y no hay idPaciente, no se puede (backend lo requiere)
    if (!this.isPatient && !idPacienteToSend) return;

    let pin: string | null = null;

    // ✅ Igual que ver detalles: SOLO PACIENTE y si detalleOculto + timbrada
    if (this.isPatient && row?.detalleOculto === true && (row as any)?.timbrada) {
      const dialogRef = this.dialog.open(PatientPinDialogComponent, {
        disableClose: false,
        autoFocus: false,
        width: '560px',
        maxWidth: '92vw',
        panelClass: ['rmd-pin-dialog-panel'],
        data: { mode: 'PACIENTE' },
      });

      pin = await firstValueFrom(dialogRef.afterClosed());

      // si cerró el dialog sin acción
      if (pin === undefined as any) return;
    }

    this.catalogsService.getRecetaByIdReceta(idReceta, idPacienteToSend, pin ?? null).subscribe({
      next: (res: string) => void downloadPrescriptionPdf(res, `receta-${(row as any)?.folio || idReceta}.pdf`),
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  async enviarRecetaPorCorreo(row: Prescription) {
    const idReceta = (row as any)?.idReceta;
    const idPacienteRow = (row as any)?.idPaciente;

    if (!idReceta) return;

    const idPacienteToSend = this.resolveIdPacienteForBackend(idPacienteRow);

    if (!this.isPatient && !idPacienteToSend) return;

    let pin: string | null = null;

    if (this.isPatient && row?.detalleOculto === true && (row as any)?.timbrada) {
      const dialogRef = this.dialog.open(PatientPinDialogComponent, {
        disableClose: false,
        autoFocus: false,
        width: '560px',
        maxWidth: '92vw',
        panelClass: ['rmd-pin-dialog-panel'],
        data: { mode: 'PACIENTE' },
      });

      pin = await firstValueFrom(dialogRef.afterClosed());

      if (pin === undefined as any) return;
    }

    this.catalogsService.getRecetaByIdReceta(idReceta, idPacienteToSend, pin ?? null).subscribe({
      next: async (res: string) => {
        try {
          const pdfBase64 = await generatePrescriptionPdfBase64(res, {
            title: 'Enviando receta...',
            subtitle: 'Estamos preparando el PDF para correo'
          });

          this.service.enviarRecetaPorCorreo({ idReceta, pdfBase64 }).subscribe({
            next: (response) => this.httpMsgHandler.handleMsg(response as any),
            error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
          });
        } catch (error) {
          this.httpMsgHandler.handleMsg(error as HttpErrorResponse);
        }
      },
      error: (err: HttpErrorResponse) => this.httpMsgHandler.handleMsg(err),
    });
  }

  printHtml(fullHtmlString: string): void {
    const printWidth = 1000;
    const printHeight = 800;

    const screenWidth = window.screen.width || 1920;
    const screenHeight = window.screen.height || 1080;

    const left = Math.max((screenWidth - printWidth) / 2, 0);
    const top = Math.max((screenHeight - printHeight) / 2, 0);

    const features = `width=${printWidth},height=${printHeight},top=${top},left=${left}`;

    const printWindow = openPrintWindow(features);

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(fullHtmlString);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  }

  deleteItem(row: Prescription) {
    const { idReceta } = row as any;

    const dialogRef = this.dialog.open(DoDeleteComponent, {
      data: {
        toDelete: 'Receta',
        id: idReceta,
        name: 'esta receta',
        actionService: (id: string) => this.service.deletePrescription(id),
      },
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (result === 1) {
        this.refreshTable();
        this.refresh();
      }
    });
  }

  showQr(row: Prescription) {
    const { idReceta } = row as any;
    this.dialog.open(PrescriptionQrComponent, {
      width: '440px',
      maxWidth: '95vw',
      data: { id: idReceta },
    });
  }

  async goToDetails(row: any) {
    const idCodificado = btoa(row.idReceta);
    const fechaCodificada = btoa(row.fechaCreacion);
    const folio = btoa(row.folio);
    const estatusReceta = btoa(row.estatusReceta);
    const idPacienteToSend = this.resolveIdPacienteForBackend(row.idPaciente);
    const idDeLpACIENTE = btoa(idPacienteToSend ?? 'null');
    const downloadpdf = btoa(String(row.generarPDF));

    let pin: string | null = null;

    if (this.isPatient && row?.detalleOculto === true && row?.timbrada) {
      const dialogRef = this.dialog.open(PatientPinDialogComponent, {
        disableClose: false,
        autoFocus: false,
        width: '560px',
        maxWidth: '92vw',
        panelClass: ['rmd-pin-dialog-panel'],
        data: { mode: 'PACIENTE' },
      });

      pin = await firstValueFrom(dialogRef.afterClosed());
    }

    if (this.control) {
      this.router.navigate(
        [
          'prescription/meds/' +
          idCodificado +
          '/' +
          fechaCodificada +
          '/' +
          folio +
          '/' +
          estatusReceta +
          '/' +
          downloadpdf +
          '/' +
          idDeLpACIENTE,
        ],
        { state: { pin } }
      ).then();
    } else {
      this.router.navigate(
        ['prescription/details/' + idCodificado + '/' + fechaCodificada + '/' + estatusReceta],
        { state: { pin } }
      ).then();
    }
  }

  private refreshTable() {
    this.paginator._changePageSize(this.paginator.pageSize);
  }
}
